import React, { useEffect, useState } from 'react';
import {
  Box, VStack, HStack, FormControl, FormLabel, Input, Textarea, Button,
  Heading, Card, CardBody, useToast, Text
} from '@chakra-ui/react';
import { ArrowBackIcon, CheckIcon } from '@chakra-ui/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { designationsAPI} from '../services/api';

interface DesignationForm {
  title: string;
  code: string;
  description: string;
  department: string;
  grade: string;
  status: 'active' | 'inactive';
}

const AddDesignation: React.FC = () => {
  const [form, setForm] = useState<DesignationForm>({
    title: '',
    code: '',
    description: '',
    department: '',
    grade: '',
    status: 'active',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    if (id) {
      designationsAPI.getById(id)
        .then((res: any) => setForm({
          title: res.data.title,
          code: res.data.code,
          description: res.data.description || '',
          department: res.data.department || '',
          grade: res.data.grade,
          status: res.data.status || 'active',
        }))
        .catch((err: any) => {
          console.error('Fetch designation error:', err.message || err);
          toast({
            title: 'Error',
            description: 'Failed to fetch designation details. Please try again.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        });
    }
  }, [id, toast]);

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      if (!form.title || !form.code || !form.grade || !form.department) {
        toast({
          title: 'Error',
          description: 'Title, code, grade, and department are required.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setIsSubmitting(false);
        return;
      }
      if (id) {
        await designationsAPI.update(id, form);
      } else {
        await designationsAPI.create(form);
      }
      navigate('/designations');
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to save designation. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/designations');
  };

  return (
    <Box>
      <Box mb={8}>
        <HStack spacing={4} mb={4}>
          <Button
            leftIcon={<ArrowBackIcon />}
            variant="ghost"
            onClick={handleCancel}
            color="gray.600"
            size="lg"
          >
            Back to Designations
          </Button>
        </HStack>
        <Heading size="lg" color="gray.800" mb={2}>
          {id ? 'Edit Designation' : 'Add Designation'}
        </Heading>
        <Text color="gray.600">
          {id ? 'Update designation details and save changes.' : 'Create a new designation for your organization.'}
        </Text>
      </Box>
      <Card>
        <CardBody p={8}>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>Title</FormLabel>
              <Input
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Code</FormLabel>
              <Input
                value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value })}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Department</FormLabel>
              <Input
                value={form.department || ''}
                onChange={e => setForm({ ...form, department: e.target.value })}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Grade</FormLabel>
              <Input
                value={form.grade}
                onChange={e => setForm({ ...form, grade: e.target.value })}
              />
            </FormControl>
            <HStack spacing={4} justify="flex-end" pt={4}>
              <Button
                variant="ghost"
                onClick={handleCancel}
                size="lg"
              >
                Cancel
              </Button>
              <Button
                colorScheme="primary"
                leftIcon={<CheckIcon />}
                onClick={handleSave}
                isLoading={isSubmitting}
                loadingText={id ? 'Updating Designation...' : 'Saving Designation...'}
                size="lg"
              >
                {id ? 'Save Changes' : 'Save Designation'}
              </Button>
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};

export default AddDesignation;